import React, { useCallback, useMemo } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { capitalizeFirstLetter } from 'utils/formattingUtil';
import { handleRenderCountriesList } from 'views/Localization/utils';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import DataTable from 'components/DataTableComponent/DataTable';
import Switch from 'components/Switch/Switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui';

import { AddedLanguagesTableProps, AddLanguagesTableData, LanguageTableRow } from './AddedLanguagesTable.types';

const AddedLanguagesTable = ({
  languagesList,
  isLoading,
  selectedLanguage,
  setIsDeleteLngModalOpen,
  setSelectedLanguage,
  setIsManageLngModalOpen,
  editSelectedLanguage
}: AddedLanguagesTableProps) => {
  const toggleLanguage = (currentLanguage: LanguageTableRow) => {
    const parsedLanguageData = languagesList.map(languageItem => {
      const { countryCode, ...restItemData } = languageItem;
      return languageItem.language === currentLanguage.language ? {
        ...restItemData,
        country: countryCode,
        isEnabled: !languageItem.isEnabled
      } : {
        ...restItemData,
        country: countryCode
      }
    })
    setSelectedLanguage({ ...currentLanguage, isEnabled: !currentLanguage.isEnabled });
    editSelectedLanguage(parsedLanguageData);
  };

  const getRowsData = useCallback(() => {
    return languagesList.reduce(
      (accumulator, currentItem: AddLanguagesTableData, index) => {
        const existingLanguage = accumulator.find(
          (item: LanguageTableRow) => item.language === currentItem.language
        );
        if (existingLanguage) {
          existingLanguage._id = index.toString();
          existingLanguage.country = [
            ...existingLanguage.country,
            currentItem.country
          ];
          existingLanguage.countryCode = [
            ...existingLanguage.countryCode,
            currentItem.countryCode
          ];
          existingLanguage.isEnabled = currentItem.isEnabled ?? false;
          existingLanguage.isoCode = currentItem.isoCode
        } else {
          accumulator.push({
            ...currentItem,
            country: [currentItem.country],
            countryCode: [currentItem.countryCode],
            isEnabled: currentItem.isEnabled ?? false,
            isoCode: currentItem.isoCode,
            _id: index.toString()
          });
        }
        return accumulator;
      },
      [] as LanguageTableRow[]
    );
  }, [languagesList]);

  const rowsData = useMemo(() => getRowsData(), [getRowsData]);

  const COLUMNS: any = [
    {
      accessorKey: 'language',
      header: 'Display Name',
      enableSorting: true,
      cell: ({ getValue }: any) => getValue(),
      size: 200
    },
    {
      accessorKey: 'isoCode',
      header: 'Language Code',
      enableSorting: true,
      cell: ({ getValue }: any) => getValue(),
      size: 200
    },
    {
      accessorKey: 'country',
      header: 'Country',
      enableSorting: true,
      sortingFn: (rowA: any, rowB: any) => {
        const countryA = rowA.original.country.join(', ').toLowerCase();
        const countryB = rowB.original.country.join(', ').toLowerCase();
        return countryA.localeCompare(countryB);
      },
      cell: ({ row }: any) => {
        const titleContent =
          row.original.country.length > 1
            ? row.original.country
              .map((item: string) =>
                capitalizeFirstLetter(item.toLowerCase())
              )
              .join(', ')
            : row.original.country;
        return (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">{handleRenderCountriesList(row.original.country)}</span>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                sideOffset={5}
                className="bg-[#5B5B5B] text-white border-none shadow-lg max-w-[400px] max-h-[300px] overflow-y-auto px-3 py-2 whitespace-normal break-words"
              >
                {titleContent}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
      size: 300
    },
    {
      accessorKey: 'isEnabled',
      header: () => (
        <div className="flex items-center justify-end gap-2 w-full">
          <span>Status</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-default text-base-mutedForeground">
                  <FontAwesomeIcon icon="fa-regular fa-circle-info" />
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="bg-[#5B5B5B] text-white border-none shadow-lg max-w-[280px] px-3 py-2"
              >
                Toggling on the switch means the language is now enabled or active.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
      cell: ({ row }: any) => {
        const languageStatus = row.original.language === selectedLanguage?.language
          ? selectedLanguage?.isEnabled
          : row.original.isEnabled;
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="ml-auto w-fit mr-[21px]">
                  <Switch
                    defaultChecked={languageStatus}
                    onCheckedChange={() => toggleLanguage(row.original)}
                    size="lg"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="bg-[#5B5B5B] text-white border-none shadow-lg max-w-[280px] px-3 py-2 whitespace-normal break-words"
              >
                Activate to make this language available for users, or deactivate to hide it
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
      size: 100
    },
    {
      accessorKey: 'actions',
      header: null,
      maxSize: 60,
      minSize: 60,
      size: 60,
      cell: ({ row }: any) => (
        <div className="max-w-[60px] w-[60px]">
          <ActionCell
            rowData={row.original}
            actions={[
              {
                label: 'Edit',
                handler: () => {
                  setIsManageLngModalOpen(true);
                  setSelectedLanguage(row.original);
                }
              },
              {
                label: 'Delete',
                handler: () => {
                  setIsDeleteLngModalOpen(true);
                  setSelectedLanguage(row.original);
                }
              }
            ]}
          />
        </div>
      )
    }
  ];

  return (
    <div className="rounded-lg overflow-hidden">
      <DataTable
        data={rowsData}
        columns={COLUMNS}
        rowIdIdentifier="_id"
        bodyDataTestId="dashLanguagesList"
        isLoading={isLoading}
        showPagination={false}
        bordered={false}
      />
    </div>
  );
};

export default AddedLanguagesTable;
